import React, { Suspense, lazy, memo, useEffect, useState } from "react";
import styled from "styled-components";
import { colors } from "../../Assets/styles/colors";
import { useTranslate } from '@tolgee/react';
import { RotatingLines, TailSpin } from "react-loader-spinner";
import _, { cloneDeep, filter, initial } from "lodash";
import validator from "validator";

import OtherParticipant from "./OtherParticipant";
import FoundContactElement from "../Chat/FoundContactElement";
import { useAuthService } from "../../Context/AuthContext";
import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import SearchMenu from "../GenericComponents/SearchMenu";
import { useNotify } from "../../Helpers/Notifications";

const EditMeetingParticipants = ({
  contacts,
  meeting,
  selectedParticipants,
  setSelectedParticipants,
  initialParticipants,
  handleBackBtn,
  handleSaveBtn,
  otherParticipants,
  setOtherParticipants,
  initialOtherParticipants,
  handleSelectParticipant,
}) => {
  const { user } = useAuthService();
  const { notifySuccess, notifyError, notifyInfo } = useNotify();

  const [searchInput, setSearchInput] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const filteredOnAppContacts =
    contacts?.on_app_contacts?.filter((item) => item.id !== user.id) || [];

  // const meetingParticipants = filteredOnAppContacts.concat(initialParticipants.filter((item) => !filteredOnAppContacts.includes(item.id)))

  const meetingParticipants = [
    ...new Map(
      [...filteredOnAppContacts, ...initialParticipants].map((item) => [
        item.id,
        item,
      ])
    ).values(),
  ];

  const [invitedSnapshotIds] = useState(
    () => new Set(selectedParticipants.map((item) => item.id))
  );

  const sortedMeetingParticipants = [...meetingParticipants].sort((a, b) => {
    const aInvited = invitedSnapshotIds.has(a.id);
    const bInvited = invitedSnapshotIds.has(b.id);
    if (aInvited === bInvited) return 0;
    return aInvited ? -1 : 1;
  });

  const handleAddOtherParticipant = (participant) => {
    let foundOther = otherParticipants.find((item) => item === participant);
    let foundSelected = initialParticipants.find(
      (item) => item.email === participant
    );

    if (foundOther || foundSelected) {
      notifyError("Email already in the list");
    } else {
      let newParticipants = cloneDeep(otherParticipants);
      newParticipants.push(participant);
      setOtherParticipants(newParticipants);
      setParticipantEmail("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent form submission (if inside a form)
      if (validator.isEmail(participantEmail)) {
        handleAddOtherParticipant(participantEmail);
      }
    }
  };

  const ceckSelectedItem = (contact) => {
    let selected = false;

    if (selectedParticipants.find((item) => item.id === contact.id)) {
      selected = true;
    } else {
      selected = false;
    }

    return selected;
  };

  const handleClickOtherParticipant = (participant) => {
    let filtered = otherParticipants.filter((item) => item !== participant);
    setOtherParticipants(filtered);
  };

  const searchMember = (list) => {
    if (searchInput === "") {
      return list;
    }
    let found = list.filter((item) =>
      item.name.toLowerCase().includes(searchInput?.toLowerCase())
    );
    return found;
  };

  const searchOtherMember = (list) => {
    if (searchInput === "") {
      return list;
    }
    let found = list.filter((item) =>
      item.toLowerCase().includes(searchInput?.toLowerCase())
    );
    return found;
  };

  const checkChanges = () => {
    const selectedIds = new Set(selectedParticipants.map((p) => String(p.id)));
    const initialIds = new Set(initialParticipants.map((p) => String(p.id)));

    const contactChanges = _.isEqual(selectedIds, initialIds);
    const otherParticipant = _.isEqual(
      otherParticipants,
      initialOtherParticipants
    );

    if (contactChanges && otherParticipant) {
      setConfirmDisabled(true);
    } else {
      setConfirmDisabled(false);
    }
  };

  useEffect(() => {
    checkChanges();
  }, [selectedParticipants, contacts, otherParticipants]);
  const { t } = useTranslate();
  return (
    <FullScreenItem>
      <Wrapper>
        <Header>
          <div
            style={{ width: "100%", display: "flex", justifyContent: "end" }}
          ></div>
          <ProfileTitle>
            <h1>{t('new_chat.select_participants')}</h1>
          </ProfileTitle>
          <div
            style={{ width: "100%", display: "flex", justifyContent: "end" }}
          >
            <CloseBtn>
              <CloseIcon onClick={handleBackBtn} />
            </CloseBtn>
          </div>
        </Header>

        <Content>
          <Group>
            <TitleWrapper>
              <h1>{t('meetings.search_participant')}</h1>
            </TitleWrapper>

            <ChatOption style={{ flexDirection: "column" }}>
              <SearchMenu value={searchInput} setvalue={setSearchInput} />
            </ChatOption>
          </Group>

          <Group>
            <TitleWrapper>
              <h1>{t('new_chat.add_participant_by_email')}</h1>
            </TitleWrapper>

            <ChatOption style={{ flexDirection: "column" }}>
              <SearchWrapper>
                <CustomInput
                  value={participantEmail}
                  placeholder={
                    t('new_chat.add_participant_email')
                  }
                  onChange={(e) => setParticipantEmail(e.currentTarget.value)}
                  onKeyDown={handleKeyDown}
                  name="participantEmail"
                  maxLength={100}
                />
                {validator.isEmail(participantEmail) && (
                  <AddEmailBtn
                    onClick={() => handleAddOtherParticipant(participantEmail)}
                  >
                    {t('general.add')}
                  </AddEmailBtn>
                )}
              </SearchWrapper>
            </ChatOption>
          </Group>

          <Group style={{ height: "100%" }}>
            <TitleWrapper>
              <h1>{t('meetings.selected_participants')}</h1>
            </TitleWrapper>
            <Suspense
              fallback={
                <LoaderWrapper>
                  <RotatingLines strokeColor={colors.purple} width="50px" />
                </LoaderWrapper>
              }
            >
              <OptionWrapper style={{ height: "100%" }}>
                {searchOtherMember(otherParticipants).map(
                  (otherParticipant, index) => (
                    <OtherParticipant
                      key={index}
                      item={otherParticipant}
                      selected={true}
                      handleSelectContact={handleClickOtherParticipant}
                    />
                  )
                )}
                {searchMember(sortedMeetingParticipants).map((contact, index) => (
                  <FoundContactElement
                    key={contact.id}
                    item={contact}
                    handleSelectContact={handleSelectParticipant}
                    selected={ceckSelectedItem(contact)}
                  />
                ))}
              </OptionWrapper>
            </Suspense>
          </Group>
        </Content>
        <PrimaryButton
          style={{ marginLeft: "auto" }}
          disabled={confirmDisabled}
          onClick={() => handleSaveBtn()}
        >
          {t('general.save')}
        </PrimaryButton>
      </Wrapper>
    </FullScreenItem>
  );
};

export default EditMeetingParticipants;
const FullScreenItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 25px;
  height: 90%;
  width: 100%;
  max-width: 644px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  gap: 20px;
  border-radius: 4px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  gap: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  overflow: auto;
  height: 100%;
`;

const ProfileTitle = styled.div`
  width: 100%;
  justify-content: center;
  display: flex;
  min-width: 185px;
  h1 {
    text-transform: capitalize;
    font-weight: 700;
    font-size: 20px;
    line-height: 32px;
    letter-spacing: 0.04px;
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  h1 {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    font-size: 12px;
    font-weight: 500;
    line-height: 22px;
    text-transform: uppercase;
  }
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 10px;
`;

const ChatOption = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  border-bottom: 1px solid #363e5180;
  &:last-child {
    border: none;
  }
  h1 {
    min-width: 75px;
    color: #ffffff99;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.40799999237060547px;
  }
  h2 {
    text-transform: capitalize;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.40799999237060547px;
  }
`;

const CustomInput = styled.input`
  background: none;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  color: ${({ theme }) => theme.menu.primaryTextColor};

  width: 100%;
  border: none;
  &:focus {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`;
const AddEmailBtn = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ theme }) => theme.primaryButton.color};
  cursor: pointer;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  svg {
    height: 28px;
    width: 28px;
    color: ${({ theme }) => theme.menu.selectedOpionBackground};
    stroke: ${({ theme }) => theme.sidebar.borderColor};
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 8px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 4px;
  gap: 8px;
  background: ${({ theme }) => theme.menu.background};
`;

const SearchInput = styled.input`
  border: none;
  background: none;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.04px;
  vertical-align: middle;
  width: 100%;
  outline: none;
`;
